/**
 * <s-product-gallery> — product page gallery helpers.
 *
 * - Mobile carousel counter: [data-gallery-track] is the horizontal scroll-snap track;
 *   [data-gallery-current] receives the 1-based index of the slide in view.
 * - Video lightbox: a trigger carrying [data-video-src] and commandfor="<dialog id>" opens the
 *   dialog declaratively; this component loads the embed URL into the dialog's
 *   iframe[data-video-frame] on click and clears it when the dialog closes, so audio stops.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const track = this.querySelector('[data-gallery-track]');

    if (track) {
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => this.#updateCounter(track));
      }, { ...opts, passive: true });
    }

    this.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-video-src]') : null;
      if (!trigger) return;
      const dialog = document.getElementById(trigger.getAttribute('commandfor') || '');
      const frame = dialog?.querySelector('iframe[data-video-frame]');
      if (frame) frame.src = trigger.dataset.videoSrc || '';
    }, opts);

    // `close` does not bubble, so listen in the capture phase.
    this.addEventListener('close', (event) => {
      const frame = event.target instanceof Element ? event.target.querySelector('iframe[data-video-frame]') : null;
      if (frame) frame.src = 'about:blank';
    }, { ...opts, capture: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #updateCounter(track) {
    const current = this.querySelector('[data-gallery-current]');
    if (!current || !track.clientWidth) return;
    const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    const index = Math.round((track.scrollLeft * direction) / track.clientWidth) + 1;
    const total = track.children.length;
    current.textContent = String(Math.min(Math.max(index, 1), total));
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
